import { ArrowLeft, Loader2, Presentation, Sparkles } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TeamSelector } from "@/components/shared";
import { useApp } from "@/context/AppContext";
import { presentationsService } from "@/lib/services";
import type { PresentationProjectCreate } from "@/types";

const TEMPLATES = [
  {
    id: "mh_branded",
    name: "MH Branded",
    description: "Official Mutually Human 2026 template with Poppins fonts and brand colors",
    preview: "bg-linear-to-br from-[#FF5500]/20 to-[#8800FF]/20",
  },
  {
    id: "mutual_standard",
    name: "Mutual Standard",
    description: "Classic branded template with orange/purple accents",
    preview: "bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20",
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Clean, content-focused design with subtle styling",
    preview: "bg-linear-to-br from-slate-700/40 to-slate-600/20",
  },
  {
    id: "dark",
    name: "Dark",
    description: "Bold dark theme with high-contrast elements",
    preview: "bg-linear-to-br from-slate-900/60 to-slate-800/40",
  },
];

export function PresentationsNewProject() {
  const navigate = useNavigate();
  const { teams } = useApp();
  const defaultTeam = teams.find((t) => t.isEveryoneTeam) || teams[0];
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState<PresentationProjectCreate>({
    name: "",
    description: "",
    templateId: "mh_branded",
    context: "",
    teamId: defaultTeam?.id,
  });

  const updateField = <K extends keyof PresentationProjectCreate>(field: K, value: PresentationProjectCreate[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setSaving(true);
    setError("");

    try {
      const payload: PresentationProjectCreate = {
        name: form.name.trim(),
        description: form.description?.trim() || undefined,
        templateId: form.templateId || undefined,
        context: form.context?.trim() || undefined,
        teamId: form.teamId,
      };
      const response = await presentationsService.createProject(payload);
      navigate(`/apps/presentations/${response.data.uuid}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 border-b border-border-primary px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/apps/presentations")}
            className="p-1.5 rounded-lg hover:bg-surface-secondary text-content-tertiary hover:text-content-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <Presentation className="w-5 h-5 text-mutual-orange" />
            <h1 className="text-lg font-semibold text-content-primary">New Presentation</h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-auto p-6">
        <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Name */}
          <div>
            <label htmlFor="presentation-name" className="block text-sm font-medium text-content-secondary mb-1.5">
              Presentation Name <span className="text-red-400">*</span>
            </label>
            <input
              id="presentation-name"
              type="text"
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              placeholder="e.g. Q1 Client Proposal, Sprint Review, Company All-Hands"
              className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary text-content-primary text-sm placeholder:text-content-tertiary focus:outline-hidden focus:ring-1 focus:ring-mutual-orange/50"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="presentation-description"
              className="block text-sm font-medium text-content-secondary mb-1.5"
            >
              Description
            </label>
            <textarea
              id="presentation-description"
              value={form.description || ""}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="Briefly describe the purpose and audience for this presentation..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary text-content-primary text-sm placeholder:text-content-tertiary focus:outline-hidden focus:ring-1 focus:ring-mutual-orange/50 resize-none"
            />
          </div>

          {/* Context */}
          <div>
            <label htmlFor="presentation-context" className="block text-sm font-medium text-content-secondary mb-1.5">
              <span className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-mutual-orange" />
                Context & Key Points
              </span>
            </label>
            <textarea
              id="presentation-context"
              value={form.context || ""}
              onChange={(e) => updateField("context", e.target.value)}
              placeholder="Paste any relevant context: meeting notes, project briefs, key data points, talking points, or specific messages you want conveyed..."
              rows={6}
              className="w-full px-3 py-2 rounded-lg bg-surface-secondary border border-border-primary text-content-primary text-sm placeholder:text-content-tertiary focus:outline-hidden focus:ring-1 focus:ring-mutual-orange/50 resize-none"
            />
            <p className="mt-1 text-xs text-content-tertiary">
              The more context you provide, the better the AI can generate relevant slides.
            </p>
          </div>

          {/* Team */}
          <TeamSelector teamId={form.teamId} onTeamChange={(id) => updateField("teamId", id)} />

          {/* Template Selection */}
          <div>
            <span className="block text-sm font-medium text-content-secondary mb-2">Template</span>
            <div className="grid grid-cols-4 gap-3">
              {TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => updateField("templateId", template.id)}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    form.templateId === template.id
                      ? "border-mutual-orange bg-mutual-orange/5 ring-1 ring-mutual-orange/30"
                      : "border-border-primary bg-surface-secondary hover:bg-surface-tertiary"
                  }`}
                >
                  <div className={`h-16 rounded-lg mb-2 ${template.preview} flex items-center justify-center`}>
                    <Presentation className="w-6 h-6 text-content-tertiary opacity-50" />
                  </div>
                  <h4 className="text-sm font-medium text-content-primary">{template.name}</h4>
                  <p className="text-xs text-content-tertiary mt-0.5">{template.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={!form.name.trim() || saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-gradient text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Create & Start
            </button>
            <button
              type="button"
              onClick={() => navigate("/apps/presentations")}
              className="px-4 py-2 rounded-lg border border-border-primary text-content-secondary text-sm hover:bg-surface-secondary transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
